import { Link } from "react-router-dom";
import { Shield, Target, Award, Users } from "lucide-react";
import { ABOUT_IMAGE, BRAND } from "../data/staticData";
import SectionHeader from "../components/SectionHeader";
import Reveal from "../components/Reveal";

const VALUES = [
  {
    icon: Shield,
    title: "Veteran Discipline",
    body:
      "Built by veterans who've operated under pressure. Discipline, repetition, and humility are the foundation of every class.",
  },
  {
    icon: Award,
    title: "USCCA Certified",
    body:
      "Our concealed-carry curriculum is aligned to USCCA standards — taught by a USCCA Certified Instructor.",
  },
  {
    icon: Target,
    title: "Skill, Not Theatrics",
    body:
      "We don't sell tactical fantasy. We build slow, deliberate, repeatable skill that holds up under stress.",
  },
  {
    icon: Users,
    title: "Family-First",
    body:
      "Confidence and responsibility are the goal. We treat every student like a member of our own family.",
  },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="relative py-24 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 flex items-center justify-center opacity-15 pointer-events-none">
          <img
            src={ABOUT_IMAGE}
            alt=""
            className="max-w-[60%] max-h-[80%] w-auto h-auto object-contain"
          />
        </div>
        <div className="absolute inset-0 hero-vignette" />
        <div className="container-fld relative z-10">
          <div className="overline mb-4">// About Frontline Defense</div>
          <h1 className="font-tactical text-5xl md:text-7xl font-bold uppercase text-white tracking-tight leading-none max-w-4xl">
            We don't sell <span className="text-[#7CFF00]">fear.</span>
            <br />
            We build <span className="text-[#7CFF00]">readiness.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-300 text-lg leading-relaxed">
            {BRAND.tagline}. Frontline Defense is a veteran-owned, USCCA-certified
            training company based in Chester, Virginia, with mobile training available throughout the region. We exist to turn ordinary
            people into confident, responsible defenders of their own lives,
            families, and homes.
          </p>
        </div>
      </section>

      <section className="container-fld py-24 grid md:grid-cols-12 gap-10 items-start">
        <Reveal className="md:col-span-5">
          <div className="overline mb-4">// Mission</div>
          <h2 className="font-tactical text-4xl md:text-5xl font-bold uppercase text-white tracking-tight leading-none">
            Defend Your Own.
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            Our motto isn't a slogan — it's an operating principle. The world
            doesn't pause when chaos arrives. The only thing standing between
            your loved ones and the worst day of your life is the work you put
            in <span className="text-white">before</span> it happens.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Every Frontline Defense course is engineered to give you that work
            — in a safe, judgement-free, repetition-driven environment.
          </p>
        </Reveal>
        <Reveal delay={150} className="md:col-span-7">
          <div className="grid sm:grid-cols-2 gap-px bg-white/10">
            {VALUES.map((v, i) => (
              <div
                key={v.title}
                data-testid={`about-value-${i}`}
                className="bg-[#0A0A0A] p-8 group hover:bg-[#151515] transition-colors"
              >
                <v.icon className="w-7 h-7 text-[#7CFF00]" strokeWidth={1.5} />
                <h3 className="font-tactical text-xl uppercase text-white font-bold mt-4 tracking-wide">
                  {v.title}
                </h3>
                <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="bg-[#0F0F0F] border-y border-white/10 py-24">
        <div className="container-fld">
          <Reveal>
            <div className="flex flex-col items-center text-center mb-10">
              <div className="overline mb-4">// The FLD Network</div>
              <img
                src={BRAND.logos.frontlineFamily}
                alt="Frontline Family"
                data-testid="about-frontline-family"
                className="h-40 md:h-56 w-auto drop-shadow-[0_0_40px_rgba(124,255,0,0.5)]"
              />
              <p className="mt-6 max-w-2xl text-zinc-400 leading-relaxed">
                Once you train with us, you're part of something bigger. Our
                graduates form a network of disciplined, prepared, mutually
                supportive defenders — and that's the FLD difference.
              </p>
            </div>
          </Reveal>
          <SectionHeader
            overline="// Lead Instructor"
            title="Darrien Raines-Boswell"
            subtitle="Veteran-led, USCCA Certified instruction. Calm under pressure, judgment-free in the classroom, and obsessed with the fundamentals that keep students safe."
            align="center"
          />
          <Reveal delay={150}>
            <div className="max-w-3xl mx-auto grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-5">
                <div className="relative aspect-[3/4] overflow-hidden corner-bracket p-2 group">
                  <img
                    src="/instructor.jpg"
                    alt="Darrien Raines-Boswell — Lead Instructor"
                    data-testid="instructor-photo"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent pointer-events-none" />
                </div>
              </div>
              <div className="md:col-span-7 space-y-5">
                <div className="flex items-center gap-3">
                  <img
                    src="/badges/uscca-certified-instructor.png"
                    alt="USCCA Certified Instructor"
                    className="h-14 w-auto"
                  />
                  <div>
                    <div className="overline">USCCA Certified</div>
                    <div className="font-tactical uppercase text-white tracking-wider text-sm mt-1">
                      Founder · Frontline Defense
                    </div>
                  </div>
                </div>
                <p className="text-zinc-300 leading-relaxed">
                  Darrien is a U.S. Army Cavalry Scout (MOS 19D) and the founder
                  of Frontline Defense. He served with{" "}
                  <span className="text-white">7-10 Cav, 2-1 Cav, and 4-2 Cav</span>,
                  bringing real-world tactical discipline and USCCA-aligned
                  instruction to every class — from brand-new shooters taking
                  their first range trip to concealed carriers refining their
                  fundamentals.
                </p>
                <p className="text-zinc-400 leading-relaxed text-sm">
                  His teaching style is calm, detail-driven, and judgment-free.
                  Students consistently say he focuses on{" "}
                  <span className="text-white">proper fundamentals and
                  realistic training</span> instead of rushing through drills.
                </p>
                <ul className="space-y-2 text-sm text-zinc-300 pt-2">
                  <li className="flex items-start gap-2">
                    <span className="text-[#7CFF00]">▸</span>
                    USCCA Certified Instructor
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#7CFF00]">▸</span>
                    U.S. Army Cavalry Scout (19D) — 7-10 Cav, 2-1 Cav, 4-2 Cav
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#7CFF00]">▸</span>
                    Specialty: Concealed Carry, Home Defense & SIRT-based dry-fire training
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#7CFF00]">▸</span>
                    Mobile training available throughout the Greater Richmond area
                  </li>
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-fld py-24 text-center">
        <Reveal>
          <img
            src={BRAND.logos.raisedOnFrontline}
            alt="Raised on the Frontline"
            data-testid="about-raised-on-frontline"
            className="h-44 md:h-64 w-auto mx-auto mb-12 drop-shadow-[0_0_44px_rgba(124,255,0,0.55)]"
          />
          <div className="overline mb-4">// Ready to Begin?</div>
          <h2 className="font-tactical text-4xl md:text-6xl font-bold uppercase text-white tracking-tight leading-none">
            The work starts <span className="text-[#7CFF00]">now.</span>
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/booking" className="btn-primary" data-testid="about-cta-book">
              Book Your First Class
            </Link>
            <Link to="/classes" className="btn-secondary" data-testid="about-cta-classes">
              View All Courses
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
